import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Popup, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, Globe, Tree, MagnifyingGlass, ChartLineUp } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function MapClickHandler({ onClick }) {
  useMapEvents({ click: (e) => onClick(e.latlng) });
  return null;
}

export default function TreeMonitorPage() {
  const { user } = useAuth();
  const [result, setResult] = useState(null);
  const [changeData, setChangeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [clickedPos, setClickedPos] = useState(null);
  const [areaKm2, setAreaKm2] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");

  const analyzeLocation = useCallback(async (lat, lon) => {
    setLoading(true);
    setClickedPos([lat, lon]);
    try {
      const [est, change] = await Promise.all([
        axios.get(`${API}/trees/estimate`, { params: { lat, lon, area_km2: areaKm2 }, withCredentials: true }),
        axios.get(`${API}/trees/change`, { params: { lat, lon, years: 5 }, withCredentials: true }),
      ]);
      setResult(est.data);
      setChangeData(change.data);
    } catch {
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [areaKm2]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    try {
      const res = await axios.get(`${API}/geocode`, { params: { q: searchQuery } });
      const loc = res.data?.results?.[0];
      if (loc) analyzeLocation(loc.lat, loc.lon);
    } catch { /* silent */ }
  };

  return (
    <div className="min-h-screen bg-stone-950 text-white" data-testid="tree-monitor-page">
      {/* Header */}
      <header className="h-14 bg-stone-900/95 border-b border-stone-800 flex items-center px-4 gap-3 sticky top-0 z-50">
        <Link to="/dashboard" className="flex items-center gap-2 text-stone-400 hover:text-white transition-colors" data-testid="trees-back-link">
          <ArrowLeft size={18} weight="bold" /><span className="text-sm font-body hidden sm:inline">Dashboard</span>
        </Link>
        <div className="w-px h-6 bg-stone-700" />
        <Tree size={18} weight="bold" className="text-emerald-400" />
        <span className="font-heading text-sm font-semibold">Tree & Forest Monitor</span>
        <div className="flex-1" />
        <div className="flex items-center gap-2 bg-stone-800 border border-stone-700 rounded-lg px-3 py-1.5">
          <MagnifyingGlass size={14} className="text-stone-400" />
          <input
            value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="Search place..." className="bg-transparent text-white text-xs w-32 focus:outline-none placeholder:text-stone-500"
            data-testid="tree-search-input"
          />
        </div>
        <div className="flex items-center gap-2 bg-stone-800 border border-stone-700 rounded-lg px-3 py-1.5">
          <span className="text-[10px] text-stone-400">Area:</span>
          <input type="number" value={areaKm2} onChange={(e) => setAreaKm2(parseFloat(e.target.value) || 1)} min={0.1} max={1000}
            className="bg-transparent text-white text-xs w-16 focus:outline-none" data-testid="tree-area-input" />
          <span className="text-[10px] text-stone-400">km²</span>
        </div>
      </header>

      <div className="flex" style={{ height: "calc(100vh - 56px)" }}>
        {/* Map */}
        <div className="flex-1 relative">
          <MapContainer center={[20, 0]} zoom={3} className="h-full w-full" zoomControl={false} style={{ background: "#1c1917" }}>
            <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap contributors' />
            <TileLayer
              url={`https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/MODIS_Terra_NDVI/default/${new Date(Date.now() - 3 * 86400000).toISOString().split("T")[0]}/GoogleMapsCompatible_Level9/{z}/{y}/{x}.png`}
              opacity={0.6} maxNativeZoom={9} maxZoom={18}
            />
            <MapClickHandler onClick={(latlng) => analyzeLocation(latlng.lat, latlng.lng)} />
            {clickedPos && (
              <CircleMarker center={clickedPos} radius={12} pathOptions={{ color: "#10b981", fillColor: "#10b981", fillOpacity: 0.3, weight: 2 }}>
                <Popup><span className="font-body text-sm font-semibold">Analysis Point</span></Popup>
              </CircleMarker>
            )}
          </MapContainer>
          <div className="absolute top-4 left-4 z-[900] bg-stone-900/90 backdrop-blur-md rounded-xl px-4 py-3 border border-stone-700">
            <p className="text-xs text-emerald-400 font-heading font-medium">Click map to analyze tree density</p>
            <p className="text-[10px] text-stone-500 mt-1">NDVI satellite overlay shows vegetation intensity</p>
          </div>
          {/* Legend */}
          <div className="absolute bottom-6 left-4 z-[900] bg-stone-900/90 backdrop-blur-md rounded-xl px-4 py-3 border border-stone-700">
            <p className="text-[10px] text-stone-400 mb-2 uppercase tracking-wider">Tree Density Scale</p>
            {[
              { color: "#8B4513", label: "Barren" }, { color: "#DEB887", label: "Very Sparse" },
              { color: "#FFD700", label: "Sparse" }, { color: "#90EE90", label: "Moderate" },
              { color: "#228B22", label: "Dense" }, { color: "#006400", label: "Very Dense" },
            ].map((d) => (
              <div key={d.label} className="flex items-center gap-2 py-0.5">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: d.color }} />
                <span className="text-[10px] text-stone-400">{d.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Results Panel */}
        <div className="w-96 bg-stone-900 border-l border-stone-800 overflow-y-auto p-5 space-y-5">
          <h2 className="font-heading text-lg font-semibold text-white">Analysis Results</h2>

          {loading ? (
            <div className="py-12 text-center">
              <div className="w-8 h-8 border-2 border-stone-600 border-t-emerald-400 rounded-full animate-spin mx-auto" />
              <p className="text-xs text-stone-500 mt-3">Analyzing satellite data...</p>
            </div>
          ) : result ? (
            <>
              {/* Density card */}
              <div className="rounded-xl p-4 border border-stone-700" style={{ backgroundColor: result.density.color + "15" }}>
                <p className="text-xs text-stone-400 uppercase tracking-wider mb-1">Tree Density</p>
                <p className="text-2xl font-heading font-bold" style={{ color: result.density.color }}>{result.density.label}</p>
                <p className="text-xs text-stone-500 mt-1">NDVI: {result.ndvi.toFixed(4)}</p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-stone-800/60 rounded-xl p-3 border border-stone-700/50">
                  <p className="text-[10px] text-stone-500 uppercase">Est. Trees</p>
                  <p className="text-lg font-heading font-semibold text-white">{result.estimated_trees.toLocaleString()}</p>
                </div>
                <div className="bg-stone-800/60 rounded-xl p-3 border border-stone-700/50">
                  <p className="text-[10px] text-stone-500 uppercase">CO2 Absorbed</p>
                  <p className="text-lg font-heading font-semibold text-emerald-400">{result.co2_absorption_tons_year}t/yr</p>
                </div>
                <div className="bg-stone-800/60 rounded-xl p-3 border border-stone-700/50">
                  <p className="text-[10px] text-stone-500 uppercase">Area</p>
                  <p className="text-lg font-heading font-semibold text-white">{result.area_km2} km²</p>
                </div>
                <div className="bg-stone-800/60 rounded-xl p-3 border border-stone-700/50">
                  <p className="text-[10px] text-stone-500 uppercase">Trees/ha</p>
                  <p className="text-lg font-heading font-semibold text-white">{result.density.trees_per_ha}</p>
                </div>
              </div>

              {/* Change chart */}
              {changeData?.timeline && (
                <div>
                  <h3 className="text-xs text-stone-400 uppercase tracking-wider mb-3">5-Year Tree Cover Change</h3>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={changeData.timeline}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#292524" />
                      <XAxis dataKey="year" tick={{ fill: "#78716c", fontSize: 10 }} axisLine={{ stroke: "#44403c" }} />
                      <YAxis tick={{ fill: "#78716c", fontSize: 10 }} axisLine={{ stroke: "#44403c" }} />
                      <Tooltip contentStyle={{ background: "#1c1917", border: "1px solid #44403c", borderRadius: "8px", fontSize: "11px", color: "#fff" }} />
                      <Bar dataKey="trees_per_ha" radius={[4, 4, 0, 0]}>
                        {changeData.timeline.map((e, i) => <Cell key={i} fill={e.color} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <div className={`mt-2 text-sm font-heading font-semibold ${changeData.net_change >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {changeData.net_change >= 0 ? "+" : ""}{changeData.change_percent}% change ({changeData.net_change >= 0 ? "increase" : "decrease"})
                  </div>
                </div>
              )}

              <p className="text-[10px] text-stone-600 font-body leading-relaxed">
                Estimates based on MODIS NDVI satellite data. Tree density calculated using vegetation index correlation models.
                CO2 absorption assumes 22kg CO2/tree/year average.
              </p>
            </>
          ) : (
            <div className="text-center py-12">
              <Tree size={40} className="text-stone-700 mx-auto mb-3" />
              <p className="text-sm text-stone-500">Click anywhere on the map to analyze tree density and forest cover</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
